import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Building2,
  BookUser,
  Check,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Search,
  Truck,
  Users,
  UsersRound,
} from 'lucide-react';
import {
  basculerFournisseur,
  creerFournisseur,
  fetchFournisseurs,
  modifierFournisseur,
  rechercherEntreprise,
  reprendreFournisseurs,
  type Fournisseur,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { euros } from '../utils/format';
import { vide as listeVide } from '../utils/listes';

const jourCourt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const vide = {
  nom: '',
  siret: '',
  categorie: 'autre',
  adresse: '',
  codePostal: '',
  ville: '',
  telephone: '',
  email: '',
  contact: '',
  siteWeb: '',
  notes: '',
};

/**
 * Le répertoire des fournisseurs.
 *
 * Il était reconstitué en regroupant les achats : cela marchait, mais mal.
 * « Brasserie du Coin » et « brasserie du coin » comptaient pour deux,
 * personne ne portait de numéro de téléphone, et retrouver chez qui l'on
 * avait pris les fûts demandait de rouvrir une facture de l'an dernier.
 *
 * La fiche existe maintenant pour de bon, et la recherche se fait sous les
 * doigts : trois lettres du nom, ou les premiers chiffres d'un SIRET relevé
 * sur un devis. Rien ne se recharge — c'est ce qui fait qu'on s'en sert.
 */
export default function FournisseursPage() {
  const { user, isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const perso = usePersonnalisation();
  const optionOuverte = (nom: string) => (perso?.options as any)?.[nom] !== false;

  const [recherche, setRecherche] = useState('');
  const [categorie, setCategorie] = useState('');
  const [inactifs, setInactifs] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [form, setForm] = useState({ ...vide });
  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);

  const peut = aLeDroit('suivre_achats');
  const estBureau =
    user?.roles?.includes('ROLE_BUREAU') === true || user?.roles?.includes('ROLE_ADMIN') === true;
  const estBenevole = user?.roles?.includes('ROLE_VOLUNTEER') === true || estBureau;

  const { data, isPending } = useQuery({
    queryKey: ['fournisseurs', inactifs],
    queryFn: () => fetchFournisseurs(inactifs),
    enabled: peut,
  });

  const enregistrer = useMutation({
    mutationFn: () =>
      enEdition === null ? creerFournisseur(form) : modifierFournisseur(enEdition, form),
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      fermer();
      queryClient.invalidateQueries({ queryKey: ['fournisseurs'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const basculer = useMutation({
    mutationFn: ({ id, actif }: { id: number; actif: boolean }) => basculerFournisseur(id, actif),
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      queryClient.invalidateQueries({ queryKey: ['fournisseurs'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const reprendre = useMutation({
    mutationFn: () => reprendreFournisseurs(),
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      queryClient.invalidateQueries({ queryKey: ['fournisseurs'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const fermer = () => {
    setOuvert(false);
    setEnEdition(null);
    setForm({ ...vide });
  };

  const ouvrirEdition = (f: Fournisseur) => {
    setEnEdition(f.id);
    setForm({
      nom: f.nom,
      siret: f.siret ?? '',
      categorie: f.categorie,
      adresse: f.adresse ?? '',
      codePostal: f.codePostal ?? '',
      ville: f.ville ?? '',
      telephone: f.telephone ?? '',
      email: f.email ?? '',
      contact: f.contact ?? '',
      siteWeb: f.siteWeb ?? '',
      notes: f.notes ?? '',
    });
    setOuvert(true);
  };

  const fournisseurs = data?.fournisseurs ?? listeVide<any>();
  const categories = data?.categories ?? {};

  // Le filtrage se fait ici plutôt qu'au serveur : la liste tient en mémoire,
  // et une requête par frappe ferait clignoter l'écran pour rien. La route de
  // recherche du serveur reste, elle, ce qui alimente les rattachements
  // depuis les autres écrans.
  const visibles = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    const chiffres = terme.replace(/\D+/g, '');

    return fournisseurs
      .filter((f) => (categorie === '' ? true : f.categorie === categorie))
      .filter((f) => {
        if (terme === '') return true;
        const texte = [f.nom, f.ville, f.contact, f.email, f.categorieLabel]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (texte.includes(terme)) return true;
        return chiffres.length >= 3 && (f.siret ?? '').startsWith(chiffres);
      })
      .sort((a, b) => {
        // Le préfixe l'emporte sur le contenu : un nom qui commence par ce
        // qu'on tape est celui qu'on cherche, celui qui le contient au milieu
        // est une coïncidence.
        if (terme !== '') {
          const pa = a.nom.toLowerCase().startsWith(terme) ? 0 : 1;
          const pb = b.nom.toLowerCase().startsWith(terme) ? 0 : 1;
          if (pa !== pb) return pa - pb;
        }
        return a.nom.localeCompare(b.nom, 'fr');
      });
  }, [fournisseurs, recherche, categorie]);

  if (authLoading || (peut && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peut) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Le répertoire des fournisseurs suit le droit de suivi des achats : un administrateur
              peut l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <BookUser className="w-7 h-7 text-primary-700 shrink-0" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Répertoire — Fournisseurs
            </h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
              Chez qui l'association achète, comment les joindre, et ce qu'elle leur a déjà pris.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setForm({ ...vide });
            setEnEdition(null);
            setOuvert(!ouvert);
          }}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouveau
        </button>
      </div>

      <Onglets estBenevole={estBenevole} droitAdherents={aLeDroit('voir_adherents') && optionOuverte('licences')} droitSponsors={aLeDroit('voir_sponsors')} />

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {/* Des années d'achats portent un nom en clair. Les retaper un par un
          serait le meilleur moyen de ne jamais ouvrir cet écran. */}
      {(data?.aReprendre ?? 0) > 0 && (
        <div className="card bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800 flex flex-wrap items-center gap-3">
          <Truck className="w-5 h-5 text-primary-700 shrink-0" />
          <p className="text-sm text-primary-900 dark:text-primary-200 flex-1 min-w-[14rem]">
            <strong>{data!.aReprendre} nom{data!.aReprendre > 1 ? 's' : ''}</strong> figure
            {data!.aReprendre > 1 ? 'nt' : ''} sur vos achats sans avoir de fiche. On peut les
            reprendre d'un geste — vous compléterez ensuite ceux que vous appelez.
          </p>
          <button
            type="button"
            onClick={() => reprendre.mutate()}
            disabled={reprendre.isPending}
            className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {reprendre.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Reprendre depuis les achats
          </button>
        </div>
      )}

      {ouvert && (
        <Formulaire
          form={form}
          setForm={setForm}
          categories={categories}
          edition={enEdition !== null}
          enCours={enregistrer.isPending}
          onEnregistrer={() => enregistrer.mutate()}
          onAnnuler={fermer}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Trois lettres du nom, une ville, ou les premiers chiffres du SIRET…"
            className="input pl-9"
          />
        </div>
        <select className="input w-auto" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
          <option value="">Toutes catégories</option>
          {Object.entries(categories).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
        <label className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
          <input type="checkbox" checked={inactifs} onChange={(e) => setInactifs(e.target.checked)} />
          Voir les inactifs
        </label>
        <p className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
          {visibles.length} sur {fournisseurs.length}
        </p>
      </div>

      {visibles.length === 0 ? (
        <div className="card text-center py-10 space-y-2">
          <Truck className="w-8 h-8 mx-auto text-gray-300" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {fournisseurs.length === 0
              ? "Aucun fournisseur pour l'instant. Le premier à créer est celui que vous appelez le plus souvent."
              : 'Aucun résultat pour cette recherche.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {visibles.map((f) => (
            <Carte
              key={f.id}
              fournisseur={f}
              onModifier={() => ouvrirEdition(f)}
              onBasculer={() => basculer.mutate({ id: f.id, actif: !f.actif })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Les quatre volets, comme sur les autres pages du répertoire. */
function Onglets({
  estBenevole,
  droitAdherents,
  droitSponsors,
}: {
  estBenevole: boolean;
  droitAdherents: boolean;
  droitSponsors: boolean;
}) {
  const volets = [
    { code: 'association', titre: 'Association', icone: Users, ouvert: estBenevole },
    { code: 'adherents', titre: 'Adhérents', icone: UsersRound, ouvert: droitAdherents },
    { code: 'fournisseurs', titre: 'Fournisseurs', icone: Truck, ouvert: true },
    { code: 'sponsors', titre: 'Sponsors', icone: Building2, ouvert: droitSponsors },
  ].filter((v) => v.ouvert);

  return (
    <div className="flex flex-wrap gap-2">
      {volets.map((v) => {
        const actif = v.code === 'fournisseurs';
        return (
          <Link
            key={v.code}
            to={`/repertoire/${v.code}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              actif
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-300'
            }`}
          >
            <v.icone className="w-4 h-4" />
            {v.titre}
          </Link>
        );
      })}
    </div>
  );
}

function Carte({
  fournisseur: f,
  onModifier,
  onBasculer,
}: {
  fournisseur: Fournisseur;
  onModifier: () => void;
  onBasculer: () => void;
}) {
  return (
    <div className={`card space-y-2.5 ${f.actif ? '' : 'opacity-60'}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{f.nom}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {f.categorieLabel}
            {f.contact ? ` · ${f.contact}` : ''}
            {!f.actif ? ' · inactif' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onModifier}
          title="Modifier la fiche"
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
        >
          <Pencil className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {f.telephone ? (
          <a
            href={`tel:${f.telephone.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300 hover:underline"
          >
            <Phone className="w-3.5 h-3.5" /> {f.telephone}
          </a>
        ) : (
          <span className="text-sm text-gray-400">Pas de téléphone</span>
        )}
        {f.email && (
          <a
            href={`mailto:${f.email}`}
            className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300 hover:underline min-w-0"
          >
            <Mail className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{f.email}</span>
          </a>
        )}
      </div>

      {f.adresseComplete && (
        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {f.adresseComplete}
        </p>
      )}

      {f.notes && (
        <p className="text-sm text-gray-600 dark:text-gray-400 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
          {f.notes}
        </p>
      )}

      {/* Ce que la comptabilité sait de lui : la moitié de l'intérêt de la
          fiche, et elle ne se saisit pas — elle se compte. */}
      <div className="pt-2 border-t border-gray-100 dark:border-gray-800 text-sm flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-gray-500 dark:text-gray-400">
          {f.achats > 0
            ? `${f.achats} achat${f.achats > 1 ? 's' : ''}${f.dernierAchat ? `, dernier le ${jourCourt.format(new Date(f.dernierAchat))}` : ''}`
            : 'Aucun achat rattaché'}
        </span>
        {f.total > 0 && <span className="font-semibold tabular-nums">{euros.format(f.total)}</span>}
      </div>

      {f.siret && (
        <p className="text-xs text-gray-400 font-mono">SIRET {f.siret}</p>
      )}

      <button
        type="button"
        onClick={onBasculer}
        className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
      >
        {f.actif ? 'Désactiver — ses achats restent' : 'Réactiver'}
      </button>
    </div>
  );
}

/**
 * La saisie d'une fiche, avec le SIRET qui remplit le reste.
 *
 * Le numéro se relève sur un devis ou un Kbis : le saisir puis recopier à la
 * main la raison sociale et l'adresse revient à retaper ce que l'annuaire des
 * entreprises sait déjà, et à y introduire des fautes.
 */
function Formulaire({
  form,
  setForm,
  categories,
  edition,
  enCours,
  onEnregistrer,
  onAnnuler,
}: {
  form: typeof vide;
  setForm: (f: typeof vide) => void;
  categories: Record<string, string>;
  edition: boolean;
  enCours: boolean;
  onEnregistrer: () => void;
  onAnnuler: () => void;
}) {
  const [cherche, setCherche] = useState(false);
  const [trouve, setTrouve] = useState<string | null>(null);
  const [rate, setRate] = useState<string | null>(null);
  const dernierCherche = useRef('');

  const chiffres = form.siret.replace(/\D+/g, '');
  const numeroComplet = chiffres.length === 14 || chiffres.length === 9;

  const remplir = async (numero: string) => {
    setCherche(true);
    setTrouve(null);
    setRate(null);

    // La recherche rend null aussi bien pour un numéro inconnu que pour un
    // annuaire injoignable : dans les deux cas la suite est la même — on
    // saisit à la main —, et distinguer les deux cas n'aiderait personne.
    const e = await rechercherEntreprise(numero);
    setCherche(false);

    if (e === null) {
      setRate(
        "Ce numéro n'a rien donné — inconnu, ou annuaire momentanément injoignable. Ce n'est pas bloquant : saisissez la fiche à la main."
      );
      return;
    }

    // Ce qui est déjà saisi n'est jamais écrasé : quelqu'un qui a pris la
    // peine de corriger un nom ne veut pas le voir remplacé par la raison
    // sociale officielle, souvent en majuscules et sans accents.
    setForm({
      ...form,
      nom: form.nom.trim() === '' ? (e.nom ?? '') : form.nom,
      siret: e.siret ?? form.siret,
      adresse: form.adresse.trim() === '' ? (e.adresse ?? '') : form.adresse,
      codePostal: form.codePostal.trim() === '' ? (e.codePostal ?? '') : form.codePostal,
      ville: form.ville.trim() === '' ? (e.ville ?? '') : form.ville,
    });
    setTrouve(e.nom ?? 'Fiche trouvée');
  };

  // Le numéro complet déclenche la recherche tout seul : demander un clic de
  // plus après avoir tapé quatorze chiffres serait une formalité de trop.
  // Le garde-fou évite de rappeler l'API pour le même numéro.
  useEffect(() => {
    if (!numeroComplet || chiffres === dernierCherche.current) return;
    dernierCherche.current = chiffres;
    const minuterie = setTimeout(() => void remplir(chiffres), 400);
    return () => clearTimeout(minuterie);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiffres, numeroComplet]);

  const pret = form.nom.trim() !== '';

  return (
    <section className="card space-y-4">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">
        {edition ? 'Modifier la fiche' : 'Nouveau fournisseur'}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          SIRET ou SIREN
          <div className="relative mt-1">
            <input
              className="input pr-10"
              value={form.siret}
              onChange={(e) => setForm({ ...form, siret: e.target.value })}
              placeholder="812 345 678 00014"
              inputMode="numeric"
            />
            {cherche && (
              <Loader2 className="w-4 h-4 animate-spin text-primary-600 absolute right-3 top-1/2 -translate-y-1/2" />
            )}
            {!cherche && trouve && (
              <Check className="w-4 h-4 text-green-600 absolute right-3 top-1/2 -translate-y-1/2" />
            )}
          </div>
          <span className="text-xs text-gray-400">
            {trouve
              ? `Reconnu : ${trouve}. Les champs vides ont été remplis.`
              : rate
                ? rate
                : 'Le numéro complet remplit le nom et l’adresse tout seul, depuis l’annuaire public des entreprises.'}
          </span>
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Nom ou raison sociale
          <input
            className="input mt-1"
            value={form.nom}
            onChange={(e) => setForm({ ...form, nom: e.target.value })}
            placeholder="Brasserie du Coin"
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Catégorie
          <select
            className="input mt-1"
            value={form.categorie}
            onChange={(e) => setForm({ ...form, categorie: e.target.value })}
          >
            {Object.entries(categories).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Personne à demander
          <input
            className="input mt-1"
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="Michel, au dépôt"
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Téléphone
          <input
            className="input mt-1"
            value={form.telephone}
            onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            inputMode="tel"
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Courriel
          <input
            className="input mt-1"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400 lg:col-span-2">
          Adresse
          <input
            className="input mt-1"
            value={form.adresse}
            onChange={(e) => setForm({ ...form, adresse: e.target.value })}
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Code postal
          <input
            className="input mt-1"
            value={form.codePostal}
            onChange={(e) => setForm({ ...form, codePostal: e.target.value })}
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Ville
          <input
            className="input mt-1"
            value={form.ville}
            onChange={(e) => setForm({ ...form, ville: e.target.value })}
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400 lg:col-span-2">
          Site web
          <input
            className="input mt-1"
            value={form.siteWeb}
            onChange={(e) => setForm({ ...form, siteWeb: e.target.value })}
            placeholder="https://…"
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400 lg:col-span-2">
          Notes internes
          <textarea
            className="input mt-1 min-h-[4rem]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Livre le jeudi seulement · ne reprend pas les fûts entamés · demander Michel"
          />
          <span className="text-xs text-gray-400">
            Souvent la ligne la plus utile de la fiche, et celle qui se perd quand le bureau change.
          </span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onEnregistrer}
          disabled={!pret || enCours}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
          {edition ? 'Enregistrer' : 'Créer la fiche'}
        </button>
        <button type="button" onClick={onAnnuler} className="text-sm text-gray-500 dark:text-gray-400">
          Annuler
        </button>
      </div>
    </section>
  );
}
